import { Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { mean } from 'lodash';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs';
import { UiModule } from '../../../components/ui.module';
import { defaultTableDefOptions } from '../../../helpers';
import { LocationService } from '../../../service/location.service';
import {
  UpdateLocationReviewDemandRequest,
  ReviewManagementBranch,
  ReviewManagementBranchLocation,
  ReviewManagementBranchLocationContainer,
} from '../../../types';
import { CellReviewManagementActivityComponent } from './cell-review-management-activity/cell-review-management-activity.component';
import { CellReviewManagementWeeklyReviewsComponent } from './cell-review-management-weekly-reviews/cell-review-management-weekly-reviews.component';

@Component({
  selector: 'app-review-management-branch-card',
  templateUrl: './review-management-branch-card.component.html',
  styleUrls: ['./review-management-branch-card.component.scss'],
  standalone: true,
  imports: [UiModule, MatTableModule, MatTooltipModule, FormsModule, RouterModule, AgGridAngular, SweetAlert2Module],
})
export class ReviewManagementBranchCardComponent {
  private locationService = inject(LocationService);

  public isUpdating = signal<boolean>(false);

  public branch = input.required<ReviewManagementBranchLocationContainer>();
  public intervals = input<ReviewManagementBranch['intervals']>([]);
  public isLoading = input<boolean>(false);
  public displayType = input<'reviews' | 'incidents'>('reviews');

  public reloadRequested = output<void>();

  public usageGrid = viewChild<AgGridAngular>(AgGridAngular);

  public tableData = computed(() => {
    const key = this.displayType() === 'reviews' ? 'reviews_count' : 'incidents_count';
    return this.branch().locations.map((loc) => ({
      ...loc,
      aggregateDisplayValue: loc.week1[key] + loc.week2[key] + loc.week3[key] + loc.week4[key],
      week1DisplayValue: loc.week1[key],
      week2DisplayValue: loc.week2[key],
      week3DisplayValue: loc.week3[key],
      week4DisplayValue: loc.week4[key],
    }));
  });

  public readonly usageColumns: ColDef<ReviewManagementBranchLocation>[] = [
    {
      field: 'external_id',
      headerName: 'External Id',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      tooltipField: 'external_id',
    },
    {
      field: 'name',
      headerName: 'Location Name',
      cellDataType: 'text',
      minWidth: 300,
      filter: 'agTextColumnFilter',
      sortable: true,
      tooltipField: 'name',
    },
    {
      headerName: 'Weekly Reviews',
      cellRenderer: CellReviewManagementWeeklyReviewsComponent,
      minWidth: 200,
      cellRendererParams: {
        disableActions: () => {
          return this.isUpdating();
        },
      },
    },
    {
      field: 'aggregateDisplayValue',
      headerName: 'Sum of Last 4 Weeks',
      sortable: true,
      sort: 'desc',
    },
    {
      field: 'week1DisplayValue',
      headerName: 'Week1',
      tooltipField: 'week1DisplayValue',
      headerTooltip: 'Week 1 Reviews',
    },
    {
      field: 'week2DisplayValue',
      headerName: 'Week2',
      tooltipField: 'week2DisplayValue',
      headerTooltip: 'Week 2 Reviews',
    },
    {
      field: 'week3DisplayValue',
      headerName: 'Week3',
      tooltipField: 'week3DisplayValue',
      headerTooltip: 'Week 3 Reviews',
    },
    {
      field: 'week4DisplayValue',
      headerName: 'Week4',
      tooltipField: 'week4DisplayValue',
      headerTooltip: 'Week 4 Reviews',
    },
    {
      headerName: '',
      cellRenderer: CellReviewManagementActivityComponent,
      maxWidth: 80,
      sortable: false,
      suppressHeaderMenuButton: true,
    },
  ];

  public readonly usageTable: GridOptions<ReviewManagementBranchLocation> = {
    ...defaultTableDefOptions(),
    rowSelection: {
      mode: 'multiRow',
    },
    selectionColumnDef: {
      maxWidth: 40,
      suppressHeaderMenuButton: true,
      cellClass: 'flex',
    },
  };

  constructor() {
    effect(() => {
      const intervals = this.intervals();
      if (intervals.length === 0) return;

      intervals.forEach((interval, i) => {
        const from = DateTime.fromISO(interval.from);
        const to = DateTime.fromISO(interval.to);

        this.usageColumns[i + 4].headerName = ` ${from.toFormat('LLL dd')} - ${to.toFormat('LLL dd')}`;
        this.usageColumns[i + 4].headerTooltip = ` ${from.toFormat('LLL dd')} - ${to.toFormat('LLL dd')}`;
      });
    });
  }

  public selectedLocationIds() {
    return (this.usageGrid()?.api?.getSelectedRows() ?? []).map((r) => r.id);
  }

  updateAllSelectedLocationCounts(value: string) {
    if (value === '') return;

    this.isUpdating.set(true);
    const finalValue = Math.max(0, +value);
    this.locationService
      .batchUpdateLocationReviewDemands({
        location_weekly_reviews: this.selectedLocationIds().reduce<UpdateLocationReviewDemandRequest>((acc, id) => {
          acc[id] = finalValue;
          return acc;
        }, {}),
      })
      .pipe(finalize(() => this.isUpdating.set(false)))
      .subscribe(() => this.reloadRequested.emit());
  }

  matchSelectedReviewCounts() {
    const desiredWeeklyReview = this.selectedLocationIds().reduce<UpdateLocationReviewDemandRequest>((acc, id) => {
      const locationData = this.tableData().find((loc) => loc.id === id);
      if (!locationData) {
        return acc;
      }

      const allReviewCounts = [
        locationData.week1.reviews_count,
        locationData.week2.reviews_count,
        locationData.week3.reviews_count,
        locationData.week4.reviews_count,
      ];

      const updatedValue = Math.floor(mean(allReviewCounts));
      acc[id] = Math.max(0, updatedValue);

      return acc;
    }, {});

    this.isUpdating.set(true);
    this.locationService
      .batchUpdateLocationReviewDemands({
        location_weekly_reviews: desiredWeeklyReview,
      })
      .pipe(finalize(() => this.isUpdating.set(false)))
      .subscribe(() => this.reloadRequested.emit());
  }
}
